import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Sparkles, Shield, TrendingUp, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";

const AGENTS = [
  { key: "analyst", label: "Market Analyst", icon: Sparkles },
  { key: "strategist", label: "Technical Strategist", icon: TrendingUp },
  { key: "risk_officer", label: "Risk Officer", icon: Shield },
  { key: "skeptic", label: "Devil's Advocate", icon: AlertTriangle },
];

export default function AiCouncil() {
  const [symbol, setSymbol] = useState("BTC");
  const [assetType, setAssetType] = useState("crypto");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get("/ai/council?limit=10").then(({ data }) => setHistory(data));
  useEffect(() => { loadHistory(); }, []);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    setCurrent(null);
    try {
      const { data } = await api.post("/ai/council", { symbol, asset_type: assetType, notes });
      setCurrent(data);
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Council run failed");
    } finally {
      setBusy(false);
    }
  };

  const signal = current?.signal || {};
  const sigColor = signal.signal === "buy" ? "bg-green-600" : signal.signal === "sell" ? "bg-red-600" : "bg-gray-500";

  return (
    <div className="space-y-8" data-testid="ai-council-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Multi-agent AI</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-3">
          <Users size={40} className="text-black" />
          The AI Council
        </h1>
        <p className="text-gray-500 mt-3 max-w-2xl">
          Five Claude Sonnet 4.5 agents debate a symbol: Analyst, Strategist, Risk Officer, Devil&apos;s Advocate, Synthesizer.
          You get a signal + confidence + agent-agreement score + risk classification.
          Advisory only — the risk engine still has final say.
        </p>
      </div>

      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="council-form-card">
        <form onSubmit={run} className="grid gap-4 md:grid-cols-4">
          <div>
            <Label>Symbol</Label>
            <Input data-testid="council-symbol" value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} />
          </div>
          <div>
            <Label>Asset type</Label>
            <Select value={assetType} onValueChange={setAssetType}>
              <SelectTrigger data-testid="council-asset"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="crypto">Crypto</SelectItem>
                <SelectItem value="stock">Stock</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Notes (optional)</Label>
            <Input data-testid="council-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What's your bias? Any context?" />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button type="submit" disabled={busy} data-testid="run-council-btn" className="rounded-full bg-brand text-white h-11 px-6">
              <Zap size={14} className="mr-2" />
              {busy ? "Convening council…" : "Ask the council"}
            </Button>
          </div>
        </form>
      </Card>

      {current && (
        <>
          <Card className="border-2 border-brand rounded-2xl p-6" data-testid="council-signal-card">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full ${sigColor} flex items-center justify-center text-white`}>
                  <p className="font-display text-lg font-bold uppercase">
                    {signal.signal || "?"}
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl font-medium">{current.symbol}</p>
                  <p className="text-sm text-gray-500 tabular-nums">
                    ${current.current_price?.toFixed(4)} · Confidence {signal.confidence != null ? Math.round(signal.confidence * 100) : 0}% · Agreement {signal.agent_agreement != null ? Math.round(signal.agent_agreement * 100) : 0}%
                  </p>
                </div>
              </div>
              <Badge className="bg-gray-100 text-gray-700 text-xs">
                {signal.risk_class || "n/a"} risk
              </Badge>
            </div>
            <p className="mt-4 text-gray-700 leading-relaxed">{signal.explanation || "Synthesis unavailable."}</p>
            {(signal.suggested_stop != null || signal.suggested_target != null) && (
              <div className="mt-4 grid grid-cols-2 gap-4 max-w-md">
                {signal.suggested_stop != null && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500">Suggested stop</p>
                    <p className="font-mono tabular-nums text-lg">${signal.suggested_stop?.toFixed(4)}</p>
                  </div>
                )}
                {signal.suggested_target != null && (
                  <div>
                    <p className="text-[10px] font-bold uppercase text-gray-500">Suggested target</p>
                    <p className="font-mono tabular-nums text-lg">${signal.suggested_target?.toFixed(4)}</p>
                  </div>
                )}
              </div>
            )}
            <p className="mt-4 text-[10px] text-gray-400">
              Advisory only. Cannot bypass your risk engine or execute orders directly.
            </p>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            {AGENTS.map(({ key, label, icon: Icon }) => {
              const view = current.agents?.[key] || {};
              return (
                <Card key={key} className="border border-gray-200 rounded-2xl p-5" data-testid={`agent-card-${key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon size={18} className="text-gray-600" />
                    <p className="font-medium">{label}</p>
                  </div>
                  {view.error ? (
                    <p className="text-xs text-red-500">Agent failed: {view.error}</p>
                  ) : (
                    <div className="text-sm text-gray-700 space-y-2">
                      {view.summary && <p>{view.summary}</p>}
                      {view.bullets && view.bullets.map((b, i) => (
                        <p key={i} className="text-xs text-gray-500 flex gap-2"><span>•</span>{b}</p>
                      ))}
                      {view.pattern && <p className="text-xs"><span className="font-mono">{view.pattern}</span></p>}
                      {view.counter_thesis && <p className="text-xs italic text-gray-600">{view.counter_thesis}</p>}
                      {view.top_risks && view.top_risks.map((r, i) => (
                        <p key={i} className="text-xs text-red-600 flex gap-2"><span>⚠</span>{r}</p>
                      ))}
                      {view.risk_class && <Badge className="bg-gray-100 text-gray-700 text-[10px]">{view.risk_class}</Badge>}
                      {view.stop_loss && <p className="text-xs">Stop: <span className="font-mono">${view.stop_loss}</span></p>}
                      {view.target && <p className="text-xs">Target: <span className="font-mono">${view.target}</span></p>}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Card className="border border-gray-200 rounded-2xl overflow-hidden" data-testid="council-history-card">
        <div className="px-6 py-4 border-b border-gray-100">
          <p className="font-medium">Recent councils</p>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 p-6">No councils yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((h) => (
              <button
                key={h._id}
                onClick={() => setCurrent(h)}
                data-testid={`council-history-${h._id}`}
                className="w-full text-left px-6 py-3 hover:bg-gray-50 flex items-center gap-3"
              >
                <span className="font-mono text-xs text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                <span className="font-medium">{h.symbol}</span>
                <span className="text-xs text-gray-500">{h.signal?.signal || "?"}</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {h.signal?.confidence != null ? `${Math.round(h.signal.confidence * 100)}%` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
